import React, { useState, useEffect, useRef } from "react";
import {
  Home,
  Users,
  Settings,
  MessageSquare,
  Ticket,
  Layers,
  ChevronLeft,
  LogOut,
  ListTodo,
} from "lucide-react";
import { NavLink, useNavigate } from "react-router-dom";
import VRSOrbitLogo from "../../Assets/VRSorbit logo 1.png";
import VRSOrbitText from "../../Assets/VRSorbit logo 2.png";
import { Logout } from "../../Services/LoginService";
import ConfirmationAlert from "../Alerts/ConfirmationAlert";
import NotificationAlert from "../Alerts/NotificationAlert";
import Cookies from "js-cookie";
import { motion, AnimatePresence } from "framer-motion";

const Sidebar = () => {
   const navigate = useNavigate();
  const [activeMenu, setActiveMenu] = useState(null);
  const panelRef = useRef(null);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [alert, setAlert] = useState(null);
  const [roleAccess, setRoleAccess] = useState({
    pm: 0,
    dev: 0,
    tester: 0,
  });
  const [taskOpen, setTaskOpen] = useState(false);
  const [createdTaskOpen, setCreatedTaskOpen] = useState(false);

  const menuItems = [
    {
      name: "Tickets",
      icon: Ticket,
      subItems: [
        { name: "All Tickets", path: "/All-Tickets" },
        { name: "New Tickets", path: "/New-Tickets" },
        { name: "Unassigned Tickets", path: "/Unassigned-Tickets" },
        { name: "Rejected Tickets", path: "/Rejected-Tickets" },
        { name: "Assigned Tickets", path: "/Assigned-Tickets" },
        { name: "In Progress Tickets", path: "/In-Progess-Tickets" },
        { name: "Ready for Review Tickets", path: "/Ready-For-Review-Tickets" },
        {
          name: "Assign To Reviewer Tickets",
          path: "/Assign-To-Reviewer-Tickets",
        },
        {
          name: "Review In Progress Tickets",
          path: "/Review-In-Progress-Tickets",
        },
        { name: "Reviewed Tickets", path: "/Reviewed-Tickets" },
        { name: "Hold Tickets", path: "/Hold-Tickets" },
        { name: "Closed Tickets", path: "/Closed-Tickets" },
        { name: "Reopen Tickets", path: "/Reopen-Tickets" },
      ],
    },
    {
      name: "Task",
      icon: ListTodo,
      subItems: [
        { name: "All Tasks", path: "/Task/All-Tasks" },
        { name: "Assigned Task", path: "/Task/Assigned-Task" },
        { name: "In Progress Task", path: "/Task/In-Progress-Task" },
        { name: "Completed Task", path: "/Task/Completed-Task" },
        { name: "Reopen Task", path: "/Task/Reopen-Task" },
        { name: "Hold Task", path: "/Task/Hold-Task" },
        // { name: "Reviewed Tickets", path: "/Reviewed-Tickets" },
        { name: "Closed Task", path: "/Task/Closed-Task" },

        {
          type: "group",
          name: "Created Task",
          children: [
            {
              name: "Tasks Created By Me",
              path: "/Created-Task/All-Created-Task",
            },
            { name: "Assigned Task", path: "/Created-Task/Assigned-Task" },
            {
              name: "In-Progress Task",
              path: "/Created-Task/In-Progress-Task",
            },
            { name: "Completed Task", path: "/Created-Task/Completed-Task" },
            { name: "Re-open Task", path: "/Created-Task/Re-Open-Task" },
            { name: "Hold Task", path: "/Created-Task/Hold-Task" },
            { name: "Closed Task", path: "/Created-Task/Closed-Task" },
          ],
        },
      ],
    },
    {
      name: "Home",
      icon: Home,
      subItems: [
        { name: "Dashboard", path: "/home" },
        { name: "Report", path: "/Report" },
      ],
    },
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setActiveMenu(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const readAccess = () => {
      const pm = Cookies.get("Orbit_PMCount");
      const dev = Cookies.get("Orbit_DevCount");
      const tester = Cookies.get("Orbit_TesterCount");

      setRoleAccess({
        pm: pm ? Number(pm) : 0,
        dev: dev ? Number(dev) : 0,
        tester: tester ? Number(tester) : 0,
      });
    };

    // Run immediately
    readAccess();

    // Re-run when cookies change
    const interval = setInterval(readAccess, 300);

    return () => clearInterval(interval);
  }, []);

  const handleMenuClick = (name, hasSubmenu) => {
    if (!hasSubmenu) return setActiveMenu(null);
    setActiveMenu((prev) => (prev === name ? null : name));
  };

  const handleLogoutClick = () => setShowLogoutConfirm(true);

  const confirmLogout = async () => {
    try {
      const res = await Logout();

      setAlert({
        type: "success",
        message: res.data?.message || "Logged out successfully!",
      });

      Cookies.remove("Orbit_Token");
      Cookies.remove("Orbit_UserName");
      Cookies.remove("Orbit_UserEmail");
      Cookies.remove("Orbit_PMCount");
      Cookies.remove("Orbit_DevCount");
      Cookies.remove("Orbit_TesterCount");

      setTimeout(() => {
        window.location.href = "/";
      }, 2500);
    } catch (err) {
       if (err.response?.status === 400) {
      navigate("/bad-request", {
        state: {
          error: err.response?.data?.message || "Bad request (400)",
        },
      });
      return;
    }
      setAlert({
        type: "error",
        message:
          err.response?.data?.message || "Logout failed. Please try again.",
      });
    }
    setShowLogoutConfirm(false);
  };

  const getFilteredTicketMenu = () => {
    const allTabs = menuItems.find((m) => m.name === "Tickets").subItems;

    if (roleAccess.pm > 0) return allTabs;

    let allowed = [];

    if (roleAccess.dev > 0) {
      allowed.push(
        "Assigned Tickets",
        "In Progress Tickets",
        "Hold Tickets",
        "Ready for Review Tickets",
        "Reviewed Tickets",
        "Closed Tickets"
      );
    }

    if (roleAccess.tester > 0) {
      allowed.push(
        "Assign To Reviewer Tickets",
        "Review In Progress Tickets",
        "Reviewed Tickets",
        "Reopen Tickets",
        "Hold Tickets",
        "Closed Tickets"
      );
    }

    allowed = [...new Set(allowed)];
    return allTabs.filter((x) => allowed.includes(x.name));
  };

  return (
    <div className="flex h-screen">
      <div className="w-20 bg-white shadow-md flex flex-col items-center py-4 border-r">
        <img src={VRSOrbitLogo} alt="Logo" className="h-10 mb-6" />
        <nav className="flex-1 flex flex-col items-center space-y-2">
          {menuItems.map(({ name, icon: Icon, subItems }) => (
            <button
              key={name}
              onClick={() => handleMenuClick(name, !!subItems)}
              className={`flex flex-col items-center justify-center w-14 h-14 rounded-lg hover:bg-blue-50 transition ${
                activeMenu === name
                  ? "bg-gradient-to-r from-[#0061A4] to-[#00ACED] text-white"
                  : "text-gray-700"
              }`}
            >
              <Icon size={22} />
              <span className="text-[10px] mt-1">{name}</span>
            </button>
          ))}
        </nav>

        <div className="mt-auto pb-2">
          <button
            onClick={handleLogoutClick}
            className="flex flex-col items-center justify-center w-14 h-14 rounded-lg text-gray-700 hover:bg-blue-50 hover:text-[#0061A4] transition group"
          >
            <LogOut size={22} className="group-hover:text-[#0061A4]" />
            <span className="text-[10px] mt-1 group-hover:text-[#0061A4]">
              Logout
            </span>
          </button>
        </div>
      </div>

      <AnimatePresence>
        {activeMenu && (
          <motion.div
            ref={panelRef}
            initial={{
              opacity: 0,
              x: -12,
            }}
            animate={{
              opacity: 1,
              x: 0,
            }}
            exit={{
              opacity: 0,
              x: -10,
            }}
            transition={{
              duration: 0.25,
              ease: [0.4, 0, 0.2, 1],
            }}
            className="w-56 bg-white border-r shadow-xl p-4 h-screen overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent"
          >
            <img
              src={VRSOrbitText}
              alt="Logo"
              className="w-[150px] h-5 mt-4 mb-6"
            />

            <div className="flex justify-between items-center mb-3">
              <h3 className="font-medium text-gray-700">{activeMenu}</h3>
              <ChevronLeft
                size={18}
                onClick={() => setActiveMenu(null)}
                className="cursor-pointer text-gray-500 hover:text-gray-700"
              />
            </div>

            {/*TICKETS MENU */}
            {activeMenu === "Tickets" &&
              getFilteredTicketMenu().map((sub) => (
                <NavLink
                  key={sub.name}
                  to={sub.path}
                  className={({ isActive }) =>
                    `block px-3 py-2 rounded-md text-sm mb-1 transition-all duration-200 hover:bg-gradient-to-r from-[#0061A4] to-[#00ACED] 
              hover:text-white ${
                isActive ? "text-gray-600 font-medium" : "text-gray-600"
              }`
                  }
                >
                  {sub.name}
                </NavLink>
              ))}

            {/*TASK / HOME*/}
            {activeMenu !== "Tickets" &&
              menuItems
                .find((m) => m.name === activeMenu)
                ?.subItems?.map((sub, i) => {
                  if (!sub.type || sub.type === "item") {
                    return (
                      <NavLink
                        key={i}
                        to={sub.path}
                        className={({ isActive }) =>
                          `block px-3 py-2 rounded-md text-sm mb-1 transition-all 
              hover:bg-gradient-to-r from-[#0061A4] to-[#00ACED] hover:text-white
              ${isActive ? "text-gray-600 font-medium" : "text-gray-600"}`
                        }
                      >
                        {sub.name}
                      </NavLink>
                    );
                  }

                  if (sub.type === "group") {
                    return (
                      <div key={i} className="mt-2">
                        {/* Dropdown Header */}
                        <button
                          onClick={() => setCreatedTaskOpen((p) => !p)}
                          className="w-full flex justify-between items-center 
              px-3 py-2 rounded-md text-sm font-medium text-gray-700 
              bg-gray-100 hover:bg-gray-200"
                        >
                          {sub.name}

                          <ChevronLeft
                            size={16}
                            className={`transition-transform ${
                              createdTaskOpen ? "rotate-90" : ""
                            }`}
                          />
                        </button>

                        {/* Dropdown Children */}
                        {createdTaskOpen && (
                          <div className="mt-1 ml-1">
                            {sub.children.map((child, j) => (
                              <NavLink
                                key={j}
                                to={child.path}
                                className={({ isActive }) =>
                                  `block px-3 py-2 rounded-md text-sm mb-1 transition-all
                      hover:bg-gradient-to-r from-[#0061A4] to-[#00ACED] hover:text-white
                      ${
                        isActive ? "text-gray-600 font-medium" : "text-gray-600"
                      }`
                                }
                              >
                                {child.name}
                              </NavLink>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  }
                  return null;
                })}
          </motion.div>
        )}
      </AnimatePresence>
      <ConfirmationAlert
        show={showLogoutConfirm}
        title="Logout Confirmation"
        message="Are you sure you want to logout?"
        onConfirm={confirmLogout}
        onCancel={() => setShowLogoutConfirm(false)}
      />

      <NotificationAlert
        show={alert !== null}
        type={alert?.type}
        message={alert?.message}
        duration={2500}
        onClose={() => setAlert(null)}
      />
    </div>
  );
};

export default Sidebar;
